import React from "react";
import {
  ThumbsDown,
  ThumbsUp,
  Timer,
  Activity,
  AlertTriangle,
} from "lucide-react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2 as Grid,
} from "@mui/material";

import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";

import { getImportanceColor, getSatisfactionColor } from "../utils/helpers";
import { TipsAndUpdates } from "@mui/icons-material";

const Metric = ({ title, value, level }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        borderRight: "2px solid #ccc",
        borderBottom: "1px solid #ccc",
        py: 1,
        px: 2,
        // boxShadow: 1,
      }}
    >
      <Typography variant="body2">{title}</Typography>
      <Typography variant="body2" color={level}>
        {value}
      </Typography>
    </Box>
  );
};

const Result = ({ result }) => {
  return (
    <Container maxWidth="md" sx={{ px: { xs: 0, md: "24px" } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: 2,
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Gauge
            value={result.speed}
            startAngle={-110}
            endAngle={110}
            height={100}
            width={200}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                fontSize: 20,
                fontWeight: "bold",
                transform: "translate(0px, 0px)",
              },
            }}
            text={({ value, valueMax }) => `${value} / ${valueMax}`}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "capitalize" }}
          >
            {result.mode} speed
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {result.description}
        </Typography>
      </Box>
      <Box sx={{ my: 4, borderBottom: "1px solid black" }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <Timer size={20} />
        <Typography
          variant="h5"
          sx={{ ml: 1, fontSize: "19px", fontWeight: "550" }}
        >
          Lab Data
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {result.data.lab_data.items.map((metric, index) => (
          <Grid item size={{ xs: 12, sm: 6 }} key={index}>
            <Metric {...metric} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ my: 4, borderBottom: "1px solid black" }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <Activity size={20} />
        <Typography
          variant="h5"
          sx={{ ml: 1, fontSize: "19px", fontWeight: "550" }}
        >
          Opportunities
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Opportunities</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Estimated Savings
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.data.opportunities.items.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.title}</TableCell>
                <TableCell align="right" sx={{ color: row.level }}>
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ my: 4, borderBottom: "1px solid black" }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <AlertTriangle size={20} />
        <Typography
          variant="h5"
          sx={{ ml: 1, fontSize: "19px", fontWeight: "550" }}
        >
          Diagnostics
        </Typography>
      </Box>
      <Box sx={{ mb: 4, pl: 2 }}>
        {result.data.diagnostics.items.map((item, index) => (
          <Typography key={index} variant="body1" color={item.level}>
            • {item.title}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Typography
          variant="body2"
          component="a"
          href={result.link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "#1E88E5", textDecoration: "underlined" }}
        >
          Get detailed {result.mode} page speed recommendations
        </Typography>
      </Box>
    </Container>
  );
};

const PageSpeed = ({ data }) => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 6, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              color: "#4B5563",
              flex: 1,
              fontSize: "22px",
              fontWeight: "bold",
            }}
          >
            Page Speed Insights
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `1px solid ${getSatisfactionColor(
                  data.passed ? "Excellent" : "Failed"
                )}`,
                bgcolor: `${getSatisfactionColor(
                  data.passed ? "Excellent" : "Failed"
                )}20`,
                px: { xs: 0.5, sm: 1 },
                py: { xs: 0, sm: 0.5 },
                borderRadius: "5px",
              }}
            >
              {data.passed ? (
                <ThumbsUp size={20} color={getSatisfactionColor("Excellent")} />
              ) : (
                <ThumbsDown size={20} color={getSatisfactionColor("Failed")} />
              )}
              <Typography
                variant="caption"
                color={getSatisfactionColor(
                  data.passed ? "Excellent" : "Failed"
                )}
              >
                {data.passed ? "Yes" : "No"}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: `${getImportanceColor(data.impact)}`,
                fontWeight: "bold",
              }}
            >
              {data.impact}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {data.description}
        </Typography>

        <Stack spacing={2} sx={{ mb: 4 }}>
          {data.results.map((item, index) => (
            <>
              <Result result={item} />
              {index < data.results.length - 1 && (
                <Box sx={{ my: 4, borderBottom: "1px solid #e0e0e0" }} />
              )}
            </>
          ))}
        </Stack>

        {!data.passed && (
          <Box>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontSize: "19px", fontWeight: "550" }}
            >
              <TipsAndUpdates
                sx={{ mr: "5px", fontSize: "15px", color: "rgb(249, 181, 86)" }}
              />
              Recommendations:
            </Typography>
            <Stack spacing={2}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ pl: 3, position: "relative" }}
              >
                • {data.recommended_action}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default PageSpeed;
